import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { Currency } from 'dinero.js'
import React, { useMemo } from 'react'
import { useQuery } from 'urql'

import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import {
  CartLandingQuery,
  CurrentUserDocument,
  GetRecommendationsDocument,
  GuestMenuItemsByIdsDocument,
  VenueLandingQuery,
} from '@/gql/graphql'

import { RecommendationCarousel } from './ReorderDrinks/RecommendationCarousel'
import { UserGreeting } from './UserGreeting'
import {
  filterRecommendedMenuItems,
  hasOrderInLast24Hours,
  RecommendedMenuItem,
} from './utils/recommendationsUtils'

interface Props {
  venue: VenueLandingQuery['guestVenue']
  cart: CartLandingQuery['getCart']
  cartFetching: boolean
}

const PersonalisedExperience = ({ cart, cartFetching, venue }: Props) => {
  const { orderingType, orderingTypeSlug } = useOrderingTypeContext()
  const { venueSlug } = useVenueContext()

  const shouldQuery = !isServer && !cartFetching

  const [{ data: userData, fetching: userDataFetching }] = useQuery({
    query: CurrentUserDocument,
    pause: !shouldQuery,
  })

  const [recommendationResult] = useQuery({
    query: GetRecommendationsDocument,
    pause: !shouldQuery,
    variables: { venueSlug },
    requestPolicy: 'cache-first',
  })

  const menuItemIds = useMemo(
    () =>
      recommendationResult.data?.getRecommendations?.items.map(
        (item) => item.itemId,
      ) ?? [],
    [recommendationResult.data],
  )

  const [menuItemsResult] = useQuery({
    query: GuestMenuItemsByIdsDocument,
    pause: !shouldQuery || menuItemIds.length === 0,
    variables: {
      venueSlug,
      menuItemIds,
      orderingType,
      tableNumber: cart?.tableNumber,
      tableArea: cart?.tableArea,
    },
    requestPolicy: 'cache-first',
  })

  const filteredMenuItems = useMemo<RecommendedMenuItem[]>(() => {
    const recommendations = recommendationResult.data?.getRecommendations
    const menuItems = menuItemsResult.data?.guestMenuItemsByIds

    const filtered = filterRecommendedMenuItems(recommendations, menuItems)
    return filtered
  }, [
    recommendationResult.data,
    menuItemsResult.data,
    recommendationResult.fetching,
    menuItemsResult.fetching,
    recommendationResult.error,
    menuItemsResult.error,
  ])

  const carouselHeading = useMemo(() => {
    const recommendations = recommendationResult.data?.getRecommendations
    const firstOrderToday = hasOrderInLast24Hours(recommendations)
    return firstOrderToday
      ? 'Start with a drink 👋'
      : 'Trending drinks for you 👋'
  }, [recommendationResult.data])

  if (!filteredMenuItems?.length || cartFetching) {
    return null
  }

  return (
    <div className="px-4 pt-4">
      <UserGreeting
        fetching={userDataFetching}
        currentUser={userData?.currentUser}
        greeting={carouselHeading}
      />

      <RecommendationCarousel
        recommendedItems={filteredMenuItems}
        venueSlug={venueSlug}
        orderingType={orderingType}
        currency={venue?.currency as Currency}
        orderingTypeSlug={orderingTypeSlug}
      />
    </div>
  )
}

export default PersonalisedExperience
